import { useEffect, useRef, useState, type DragEvent } from 'react';
import { ArrowDown, ArrowUp, GripVertical, MoreHorizontal, Pencil, RotateCcw } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  cn,
} from '@helios/ui';
import type { NavDomain } from '@/navigation/types';

interface SidebarMenuEditorProps {
  /** The caller applies permissions, visibility, and saved labels before editing. */
  groups: NavDomain[];
  onMove: (destinationId: string, targetDomainId: string, beforeDestinationId?: string) => void;
  onRename: (destinationId: string, label: string) => void;
  onReset: () => void;
}

interface DropTarget {
  domainId: string;
  beforeDestinationId?: string;
}

const actionClass =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Editing is deliberately separate from route links so a drag or rename cannot navigate. */
export function SidebarMenuEditor({ groups, onMove, onRename, onReset }: SidebarMenuEditorProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const dragRef = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(null);
  const [labelError, setLabelError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const renameButtons = useRef(new Map<string, HTMLButtonElement>());
  const configureButtons = useRef(new Map<string, HTMLButtonElement>());
  const returnFocusId = useRef<string | null>(null);
  const editingId = editing?.id;

  useEffect(() => {
    if (editingId) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (returnFocusId.current) {
      renameButtons.current.get(returnFocusId.current)?.focus();
      returnFocusId.current = null;
    }
  }, [editingId]);

  const clearDrag = () => {
    dragRef.current = null;
    setDraggedId(null);
    setDropTarget(null);
  };

  const movePage = (destinationId: string, targetDomainId: string, beforeDestinationId?: string) => {
    const item = groups.flatMap((group) => group.items).find((page) => page.id === destinationId);
    const target = groups.find((group) => group.id === targetDomainId);
    if (!item || !target || destinationId === beforeDestinationId) return;
    const before = target.items.find((page) => page.id === beforeDestinationId);
    if (beforeDestinationId && !before) return;
    onMove(destinationId, targetDomainId, beforeDestinationId);
    setAnnouncement(before
      ? `${item.label} moved before ${before.label} in ${target.label}.`
      : `${item.label} moved to the end of ${target.label}.`);
  };

  const acceptDrag = (event: DragEvent<HTMLElement>, target: DropTarget) => {
    // Only a row dragged from this editor may mutate the menu. External drops are ignored.
    if (!dragRef.current || dragRef.current === target.beforeDestinationId
      || !groups.some((group) => group.items.some((item) => item.id === dragRef.current))) {
      setDropTarget(null);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setDropTarget(target);
  };

  const dropPage = (event: DragEvent<HTMLElement>, target: DropTarget) => {
    const sourceId = dragRef.current;
    if (!sourceId) return;
    event.preventDefault();
    event.stopPropagation();
    movePage(sourceId, target.domainId, target.beforeDestinationId);
    clearDrag();
  };

  const startRename = (id: string, label: string) => {
    clearDrag();
    setLabelError('');
    setEditing({ id, label });
  };

  const closeRename = () => {
    returnFocusId.current = editing?.id ?? null;
    setEditing(null);
    setLabelError('');
  };

  const saveRename = () => {
    if (!editing) return;
    const label = editing.label.trim();
    if (!label || label.length > 80) {
      setLabelError('Enter a label with 1–80 characters.');
      inputRef.current?.focus();
      return;
    }
    onRename(editing.id, label);
    setAnnouncement(`Page label saved as ${label}.`);
    closeRename();
  };

  const isTarget = (domainId: string, beforeDestinationId?: string) =>
    dropTarget?.domainId === domainId && dropTarget.beforeDestinationId === beforeDestinationId;

  return (
    <div
      className="space-y-2"
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropTarget(null);
      }}
    >
      <div className="px-2 pb-1">
        <div className="flex items-center justify-between gap-1">
          <p className="text-xs font-semibold text-foreground">Customize menu</p>
          <Button
            variant="ghost"
            size="xs"
            className="gap-1 px-1.5 text-muted-foreground"
            onClick={() => {
              clearDrag();
              setEditing(null);
              setLabelError('');
              onReset();
              setAnnouncement('Menu order and labels reset.');
            }}
            aria-label="Reset menu"
            title="Restore default menu order and page labels"
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
            Reset
          </Button>
        </div>
        <p className="text-2xs leading-relaxed text-muted-foreground">
          Drag pages to reorder or change sections. Use the pencil to rename. Changes save automatically.
        </p>
      </div>

      <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </p>

      <div className="space-y-1">
        {groups.map((group) => {
          const GroupIcon = group.icon;
          return (
            <section key={group.id} aria-label={group.label}>
              <h3
                onDragOver={(event) => acceptDrag(event, { domainId: group.id })}
                onDrop={(event) => dropPage(event, { domainId: group.id })}
                className={cn(
                  'flex h-7 items-center gap-2 rounded-md bg-secondary/40 px-2 text-2xs font-semibold uppercase tracking-widest text-foreground/85 transition-colors',
                  isTarget(group.id) && 'bg-primary/10 text-primary ring-1 ring-inset ring-primary',
                )}
              >
                <GroupIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{group.label}</span>
                <span className="ml-auto font-mono text-muted-foreground/50" aria-hidden="true">
                  {group.items.length}
                </span>
              </h3>
              <ul className="mt-0.5 space-y-px" aria-label={`${group.label} pages`}>
                {group.items.map((item, index) => {
                  const Icon = item.icon;
                  const renaming = editing?.id === item.id;
                  return (
                    <li
                      key={item.id}
                      data-nav-editor-item={item.id}
                      draggable={!renaming}
                      onDragStart={(event) => {
                        if (renaming || (event.target as HTMLElement).closest('button, input, form')) {
                          event.preventDefault();
                          return;
                        }
                        dragRef.current = item.id;
                        setDraggedId(item.id);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', item.id);
                        setAnnouncement(`Moving ${item.label}. Drop before a page or at the end of a section.`);
                      }}
                      onDragEnd={clearDrag}
                      onDragOver={(event) => acceptDrag(event, { domainId: group.id, beforeDestinationId: item.id })}
                      onDrop={(event) => dropPage(event, { domainId: group.id, beforeDestinationId: item.id })}
                      className={cn(
                        'rounded-md border-t-2 border-transparent transition-colors',
                        draggedId === item.id && 'opacity-40',
                        isTarget(group.id, item.id) && 'border-t-primary bg-primary/10',
                      )}
                    >
                      {renaming ? (
                        <form
                          className="space-y-1.5 px-1.5 py-1.5"
                          onSubmit={(event) => {
                            event.preventDefault();
                            saveRename();
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                              event.preventDefault();
                              event.stopPropagation();
                              closeRename();
                            }
                          }}
                        >
                          <Input
                            ref={inputRef}
                            aria-label={`Page label for ${item.label}`}
                            aria-describedby={labelError ? `nav-label-error-${item.id}` : undefined}
                            value={editing.label}
                            onChange={(event) => {
                              setEditing({ id: item.id, label: event.target.value });
                              setLabelError('');
                            }}
                            maxLength={80}
                            invalid={Boolean(labelError)}
                            className="h-8 px-2 text-xs"
                          />
                          {labelError ? (
                            <p id={`nav-label-error-${item.id}`} role="alert" className="text-2xs text-critical">
                              {labelError}
                            </p>
                          ) : null}
                          <div className="flex gap-1">
                            <Button size="xs" type="submit">Save</Button>
                            <Button size="xs" variant="ghost" onClick={closeRename}>Cancel</Button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex min-h-8 items-center gap-1 pl-1 pr-0.5 text-xs hover:bg-secondary/40">
                          <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground/50 active:cursor-grabbing" aria-hidden="true" />
                          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                          <span className="min-w-0 flex-1 truncate text-foreground/90" title={item.label}>{item.label}</span>
                          <button
                            ref={(button) => {
                              if (button) renameButtons.current.set(item.id, button);
                              else renameButtons.current.delete(item.id);
                            }}
                            type="button"
                            className={actionClass}
                            aria-label={`Rename ${item.label}`}
                            title={`Rename ${item.label}`}
                            onClick={() => startRename(item.id, item.label)}
                          >
                            <Pencil className="h-3 w-3" aria-hidden="true" />
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                ref={(button) => {
                                  if (button) configureButtons.current.set(item.id, button);
                                  else configureButtons.current.delete(item.id);
                                }}
                                type="button"
                                className={actionClass}
                                aria-label={`Configure ${item.label}`}
                                title="Move page or restore its label"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              side="right"
                              align="start"
                              className="max-h-[70vh] overflow-y-auto"
                              onCloseAutoFocus={(event) => {
                                // A section move remounts the row; focus its current control.
                                event.preventDefault();
                                configureButtons.current.get(item.id)?.focus();
                              }}
                            >
                              <DropdownMenuLabel className="max-w-60 truncate">{item.label}</DropdownMenuLabel>
                              <DropdownMenuItem disabled={index === 0} onSelect={() => movePage(item.id, group.id, group.items[index - 1]?.id)}>
                                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                                Move up
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={index === group.items.length - 1} onSelect={() => movePage(item.id, group.id, group.items[index + 2]?.id)}>
                                <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                                Move down
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Move to section</DropdownMenuLabel>
                              {groups.filter((target) => target.id !== group.id).map((target) => (
                                <DropdownMenuItem key={target.id} onSelect={() => movePage(item.id, target.id)}>
                                  Move to {target.label}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => {
                                onRename(item.id, '');
                                setAnnouncement(`Default label restored for ${item.label}.`);
                              }}>
                                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                                Restore default label
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div
                data-nav-editor-drop-section={group.id}
                onDragOver={(event) => acceptDrag(event, { domainId: group.id })}
                onDrop={(event) => dropPage(event, { domainId: group.id })}
                className={cn(
                  'flex items-center justify-center rounded-md border border-dashed border-transparent text-2xs text-muted-foreground transition-colors',
                  draggedId || group.items.length === 0 ? 'my-1 min-h-7 border-border/60' : 'h-2',
                  isTarget(group.id) && 'border-primary bg-primary/10 text-primary',
                )}
              >
                {group.items.length === 0 ? 'Drop a page here' : draggedId ? 'Drop at end of section' : null}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
